import { useState, useEffect, useMemo } from "react";
import { ArrowLeft, Save, CreditCard } from "lucide-react";
import { useSuppliers } from "../context/SuppliersContext";
import { usePaymentVoucher } from "../context/PaymentVoucherContext";
import { useNavigate, useParams } from "react-router-dom";

export default function EditPaymentVoucher() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { suppliers } = useSuppliers();
  const {
    paymentVouchers,
    fetchPaymentVouchers,
    updatePaymentVoucher,
    loading,
  } = usePaymentVoucher();

  const [voucher, setVoucher] = useState(null);
  const [initError, setInitError] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [amountPaid, setAmountPaid] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const outstanding = Number(selectedSupplier?.outstanding ?? voucher?.amount ?? 0);
  const paid        = Number(amountPaid || 0);
  const balance     = outstanding + paid;

  // Ensure the list is loaded (covers a direct page reload/deep link).
  useEffect(() => {
    if (!paymentVouchers || paymentVouchers.length === 0) {
      fetchPaymentVouchers().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Find the voucher once the list is available.
  useEffect(() => {
    if (!paymentVouchers || paymentVouchers.length === 0) return;

    const found = paymentVouchers.find((v) => String(v.id) === String(id));

    if (!found) {
      setInitError("Voucher not found");
      return;
    }

    setInitError(null);
    setVoucher(found);
    setAmountPaid(String(found.paid ?? ""));
    setNote(found.note && found.note !== "-" ? found.note : "");
  }, [id, paymentVouchers]);

  // Resolve supplier from the voucher's user_id once suppliers are available.
  useEffect(() => {
    if (!voucher || !suppliers?.length) return;
    const supplierId = voucher.user_id ?? voucher.userId;
    const match = suppliers.find((s) => String(s.id) === String(supplierId));
    if (match) setSelectedSupplier(match);
  }, [voucher, suppliers]);

  const handleSubmit = async () => {
    if (!amountPaid) return alert("Enter amount paid");

    const payload = {
      item_id:  voucher?.item_id ?? 1,
      quantity: voucher?.quantity ?? 1,
      price:    voucher?.price ?? 0,
      amount:   outstanding,
      paid,
      balance,
      note:     note || "-",
    };

    if (selectedSupplier) {
      payload.user_id = selectedSupplier.id;
    }

    try {
      setSaving(true);
      await updatePaymentVoucher(id, payload);
      alert("Payment Voucher Updated Successfully");
      navigate("/payment-voucher");
    } catch (err) {
      alert(err.message || "Error updating voucher");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !voucher) {
    return (
      <main className="min-h-screen bg-gray-50 p-4 md:p-6 font-sans flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading voucher…</p>
      </main>
    );
  }

  if (initError) {
    return (
      <main className="min-h-screen bg-gray-50 p-4 md:p-6 font-sans flex flex-col items-center justify-center gap-4">
        <p className="text-red-500 text-sm">{initError}</p>
        <button
          onClick={() => navigate("/payment-voucher")}
          className="flex items-center gap-2 border border-gray-200 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={15} /> Back to Vouchers
        </button>
      </main>
    );
  }

  if (!voucher) {
    return (
      <main className="min-h-screen bg-gray-50 p-4 md:p-6 font-sans flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading voucher…</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 p-4 md:p-6 font-sans">
      <div className="mx-auto max-w-5xl space-y-5">

        {/* Header */}
        <div className="flex justify-between items-center bg-white p-5 rounded-2xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 flex items-center justify-center rounded-xl bg-teal-600 text-white">
              <CreditCard size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Edit Payment Voucher</h1>
              <p className="text-sm text-gray-400">Update the payment details below</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/payment-voucher")}
            className="flex items-center gap-2 border border-gray-200 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={15} /> Back
          </button>
        </div>

        {/* Payment Form */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 bg-white">
            <h2 className="font-semibold text-gray-700">Payment Information</h2>
            <p className="text-sm text-gray-400 mt-0.5">Voucher #{id}</p>
          </div>

          <div className="p-6 space-y-6">

            {/* Supplier Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <InfoCard
                label="Supplier Name"
                value={
                  selectedSupplier
                    ? `${selectedSupplier.first_name} ${selectedSupplier.last_name}`
                    : voucher?.customername || "—"
                }
              />
              <InfoCard label="Phone" value={selectedSupplier?.phone || "—"} />
              <InfoCard label="Email" value={selectedSupplier?.email || "—"} />
              <InfoCard label="Outstanding" value={`₹ ${outstanding}`} highlight />
            </div>

            {/* Inputs */}
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Amount Paid">
                <input
                  type="text"
                  inputMode="numeric"
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)}
                  className="w-full mt-2 h-11 border border-gray-200 rounded-xl px-4 bg-gray-50 text-sm outline-none focus:border-teal-400"
                />
              </Field>

              <Field label="Balance">
                <input
                  value={balance}
                  readOnly
                  className="w-full mt-2 h-11 border border-gray-100 rounded-xl px-4 bg-gray-50 text-sm text-gray-500 cursor-not-allowed"
                />
              </Field>
            </div>

            {/* Note */}
            <Field label="Note">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full mt-2 border border-gray-200 rounded-xl p-3 bg-gray-50 text-sm outline-none focus:border-teal-400 resize-none"
              />
            </Field>

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t border-gray-100 pt-5">
              <button
                onClick={() => navigate("/payment-voucher")}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 rounded-xl text-white text-sm font-medium bg-teal-600 hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={14} />
                {saving ? "Saving..." : "Update Voucher"}
              </button>
            </div>

          </div>
        </div>

      </div>
    </main>
  );
}

function InfoCard({ label, value, highlight }) {
  return (
    <div className={`p-3 rounded-xl border ${highlight ? "bg-red-50 border-red-100" : "bg-white border-gray-100"}`}>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="font-semibold text-gray-800 mt-0.5">{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      {children}
    </div>
  );
}